/**
 * A modal sheet built on the native `<dialog>` element, so focus trapping, the
 * Escape key and the top layer come from the browser rather than from us.
 *
 * On narrow screens it docks to the bottom of the viewport and can be dismissed
 * by tapping the backdrop — the behaviour a phone user expects — while on a
 * desktop it centres.
 */

import { useEffect, useRef, type JSX, type ReactNode } from 'react';
import { CloseIcon } from './Icons';
import { IconButton } from './IconButton';

export interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Optional row of actions pinned to the bottom of the sheet. */
  footer?: ReactNode;
  /** Wider sheet, for the grade report. */
  wide?: boolean;
}

export function Modal({ open, title, onClose, children, footer, wide }: ModalProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // `showModal` puts focus on the first focusable child; a sheet with a
      // default action wants it on that instead.
      dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={wide ? 'modal modal--wide' : 'modal'}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // Escape is handled here as well as through the platform's own close
      // request, because that request does not always arrive. Chromium issue
      // 41484805: the click that opened the dialog counts as an activation, and
      // the close watcher the dialog creates consumes it, so a sheet opened by a
      // click and not otherwise touched gets `keydown` but never `cancel`. A
      // sheet you cannot dismiss with Escape is a trap, so both paths close it.
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }
      }}
      onClick={(e) => {
        // A click that lands on the dialog element itself is a backdrop click:
        // the content sits inside a child, so it never reports as the target.
        if (e.target === ref.current) onClose();
      }}
      aria-label={title}
    >
      <div className="modal__body">
        <header className="modal__head">
          <h2>{title}</h2>
          <IconButton label="Close" onClick={onClose} icon={<CloseIcon />} />
        </header>
        <div className="modal__content">{children}</div>
        {footer && <footer className="modal__foot">{footer}</footer>}
      </div>
    </dialog>
  );
}
